/**
 * Admin hook for the LLM telemetry rollup (INV-105b dashboard). Reads the last N days
 * of `llm_usage_daily` (best-effort per-call rollup written by the edge telemetry
 * wrapper) for the "AI Cost & Health" admin screen.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import type { Database } from '@/lib/supabase/types';

export type LlmUsageRow = Database['public']['Tables']['llm_usage_daily']['Row'];

export function useLlmUsageDaily(days = 30) {
  return useQuery({
    queryKey: ['admin', 'llm-usage', days],
    queryFn: async (): Promise<LlmUsageRow[]> => {
      const since = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('llm_usage_daily')
        .select('*')
        .gte('day', since)
        .order('day', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: isSupabaseConfigured(),
    staleTime: 60 * 1000,
  });
}

export interface LlmUsageSummary {
  totalCostUsd: number;
  totalCalls: number;
  totalErrors: number;
  errorRate: number; // 0..1
  avgLatencyMs: number;
  byModel: { model: string; provider: string; calls: number; costUsd: number }[];
  byDay: { day: string; costUsd: number; calls: number }[];
}

/** Aggregate the daily rows into the figures the dashboard shows. Pure. */
export function summarizeLlmUsage(rows: LlmUsageRow[]): LlmUsageSummary {
  let totalCostUsd = 0;
  let totalCalls = 0;
  let totalErrors = 0;
  let totalLatency = 0;
  const modelMap = new Map<string, { model: string; provider: string; calls: number; costUsd: number }>();
  const dayMap = new Map<string, { day: string; costUsd: number; calls: number }>();

  for (const r of rows) {
    totalCostUsd += Number(r.cost_usd) || 0;
    totalCalls += r.calls || 0;
    totalErrors += r.errors || 0;
    totalLatency += Number(r.total_latency_ms) || 0;

    const mk = `${r.provider}/${r.model}`;
    const m = modelMap.get(mk) ?? { model: r.model, provider: r.provider, calls: 0, costUsd: 0 };
    m.calls += r.calls || 0;
    m.costUsd += Number(r.cost_usd) || 0;
    modelMap.set(mk, m);

    const d = dayMap.get(r.day) ?? { day: r.day, costUsd: 0, calls: 0 };
    d.costUsd += Number(r.cost_usd) || 0;
    d.calls += r.calls || 0;
    dayMap.set(r.day, d);
  }

  return {
    totalCostUsd,
    totalCalls,
    totalErrors,
    errorRate: totalCalls > 0 ? totalErrors / totalCalls : 0,
    avgLatencyMs: totalCalls > 0 ? totalLatency / totalCalls : 0,
    byModel: [...modelMap.values()].sort((a, b) => b.costUsd - a.costUsd),
    byDay: [...dayMap.values()].sort((a, b) => a.day.localeCompare(b.day)),
  };
}
